/* Kingly i18n — English by default; Spanish via a dictionary applied to the rendered DOM.
   Only text nodes + a few attributes are touched (nodeValue only), so React stays in charge of the tree. */
import { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import es from "./es.json";
import { PATTERNS } from "./patterns";
import { request, wants, cached, setMTEnabled } from "./mt";

let currentLang = "en";
export const locale = () => (currentLang === "es" ? "es-MX" : "en-US");
export const getLang = () => currentLang;

const dict = new Map(Object.entries(es));
const ATTRS = ["placeholder", "title", "aria-label", "alt"];
const SKIP = new Set(["SCRIPT", "STYLE", "TEXTAREA", "INPUT", "CODE", "PRE", "NOSCRIPT"]);

/** Translate a bare string (exact dictionary hit, then regex patterns). Returns null when nothing matches. */
export function trString(raw) {
  if (!raw) return null;
  const key = raw.trim();
  if (!key || !/[A-Za-z]/.test(key)) return null;
  let v = dict.get(key);
  if (v === undefined) {
    for (const [re, rep] of PATTERNS) {
      if (re.test(key)) { v = key.replace(re, typeof rep === "function" ? rep : rep); break; }
    }
  }
  if (v === undefined || v === key) return null;
  return raw.replace(key, v);
}

/** t() for code paths that build strings before they reach the DOM (toasts, document.title). */
export const t = (s, vars) => {
  let out = currentLang === "es" ? (trString(s) ?? s) : s;
  if (vars) for (const [k, val] of Object.entries(vars)) out = out.split(`{${k}}`).join(String(val));
  return out;
};

const seen = new WeakMap(); // text node → last value we wrote

function applyMT(node, val) {
  const target = currentLang;
  request(val, target, (out) => {
    if (!node.isConnected || node.nodeValue !== val || currentLang !== target || out === val.trim()) return;
    const nv = val.replace(val.trim(), out);
    seen.set(node, nv); node.nodeValue = nv;
    const p = node.parentElement;
    if (p) { p.setAttribute("data-mt-original", val.trim()); if (!p.hasAttribute("title")) { p.setAttribute("title", val.trim()); p.setAttribute("data-i18n-title", val.trim()); } }
  });
}

function translateTextNode(node) {
  const val = node.nodeValue;
  if (seen.get(node) === val) return; // our own write
  const p = node.parentElement;
  if (!p || SKIP.has(p.tagName) || p.closest("[data-no-translate]")) return;
  if (currentLang === "es") {
    const tr = trString(val);
    if (tr !== null) { seen.set(node, tr); node.nodeValue = tr; return; }
  }
  if (wants(val, currentLang)) applyMT(node, val);
}

function translateElement(el) {
  if (currentLang !== "es" || !(el instanceof Element) || el.closest("[data-no-translate]")) return;
  for (const a of ATTRS) {
    if (!el.hasAttribute(a)) continue;
    const v = el.getAttribute(a); const prev = el.getAttribute(`data-i18n-${a}`);
    if (prev === v) continue;
    const tr = trString(v);
    if (tr !== null) { el.setAttribute(a, tr); el.setAttribute(`data-i18n-${a}`, tr); }
  }
}

function walk(root) {
  if (root.nodeType === Node.TEXT_NODE) { const p = root.parentElement; if (p && !SKIP.has(p.tagName) && !p.closest("[data-no-translate]")) translateTextNode(root); return; }
  if (root.nodeType !== Node.ELEMENT_NODE || SKIP.has(root.tagName)) return;
  translateElement(root);
  const tw = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
    acceptNode: (n) => (n.nodeType === Node.ELEMENT_NODE ? (SKIP.has(n.tagName) || n.hasAttribute("data-no-translate") ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT) : NodeFilter.FILTER_ACCEPT),
  });
  let n;
  while ((n = tw.nextNode())) { if (n.nodeType === Node.TEXT_NODE) translateTextNode(n); else translateElement(n); }
}

let observer = null;
function startDom() {
  if (observer) return;
  walk(document.body);
  observer = new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.type === "characterData") translateTextNode(m.target);
      else if (m.type === "attributes") translateElement(m.target);
      else m.addedNodes.forEach((n) => walk(n));
    }
  });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
}
function stopDom() { if (observer) { observer.disconnect(); observer = null; } }

/* Long-press on a machine-translated line shows the original wording (hover uses the title attribute). */
let tip = null, pressTimer = null;
function hideTip() { clearTimeout(pressTimer); pressTimer = null; if (tip) { tip.remove(); tip = null; } }
function startLongPress() {
  if (typeof document === "undefined" || document.__kinglyMtPress) return;
  document.__kinglyMtPress = true;
  document.addEventListener("touchstart", (e) => {
    const el = e.target.closest?.("[data-mt-original]");
    if (!el) return;
    const touch = e.touches[0];
    pressTimer = setTimeout(() => {
      hideTip();
      tip = document.createElement("div");
      tip.setAttribute("data-no-translate", "");
      tip.setAttribute("data-testid", "mt-original-tip");
      tip.textContent = el.getAttribute("data-mt-original");
      tip.style.cssText = `position:fixed;left:${Math.max(8, Math.min(touch.clientX - 120, window.innerWidth - 248))}px;top:${Math.max(8, touch.clientY - 70)}px;max-width:240px;z-index:9999;background:#0f172a;color:#fff;font-size:13px;line-height:1.3;padding:8px 10px;border-radius:10px;box-shadow:0 8px 24px rgba(0,0,0,.35);pointer-events:none`;
      document.body.appendChild(tip);
    }, 450);
  }, { passive: true });
  ["touchend", "touchcancel", "touchmove"].forEach((ev) => document.addEventListener(ev, hideTip, { passive: true }));
}

/** Translation of a typed string into the current language, or null when none is needed/available yet. */
export function useMT(text) {
  const { lang } = useLang();
  const clean = (text || "").trim();
  const [out, setOut] = useState(() => cached(clean, lang) ?? null);
  useEffect(() => {
    if (!clean || !wants(clean, lang)) { setOut(null); return undefined; }
    let live = true;
    setOut(cached(clean, lang) ?? null);
    request(clean, lang, (v) => { if (live) setOut(v); });
    return () => { live = false; };
  }, [clean, lang]);
  return out && out !== clean ? out : null;
}

const LangCtx = createContext({ lang: "en", setLang: () => {}, t });
export const useLang = () => useContext(LangCtx);

const STORAGE = "kingly.lang";
const initial = () => {
  try {
    const q = new URLSearchParams(window.location.search).get("lang");  // public links texted in Spanish carry ?lang=es
    if (q === "es" || q === "en") { localStorage.setItem(STORAGE, q); return q; }
    return localStorage.getItem(STORAGE) === "es" ? "es" : "en";
  } catch { return "en"; }
};

/** Wrap the app once; `remountKey` changes on EN↔ES so React re-renders every string fresh. */
export function LanguageProvider({ children, userLang, onPersist, mtEnabled = false }) {
  const [lang, setLangState] = useState(initial);
  const [epoch, setEpoch] = useState(0);
  useEffect(() => { setMTEnabled(mtEnabled); if (mtEnabled) startLongPress(); }, [mtEnabled]);
  useEffect(() => {  // profile wins — unless the person already picked a language on the login screen
    if (!userLang) return;
    let explicit = false;
    try { explicit = localStorage.getItem(STORAGE + ".explicit") === "1"; localStorage.removeItem(STORAGE + ".explicit"); } catch {}
    if (explicit && userLang !== lang) { onPersist?.(lang); return; }
    if (userLang !== lang) { stopDom(); setLangState(userLang); setEpoch((e) => e + 1); }
  }, [userLang]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    currentLang = lang;
    document.documentElement.lang = lang;
    try { localStorage.setItem(STORAGE, lang); } catch {}
    if (lang === "es" || mtEnabled) { stopDom(); startDom(); } else stopDom();
    return () => stopDom();
  }, [lang, epoch, mtEnabled]);
  const setLang = useCallback((l, { persist = true } = {}) => {
    if (l === lang) return;
    stopDom(); setLangState(l); setEpoch((e) => e + 1);
    if (!persist) return;
    if (userLang) onPersist?.(l); else { try { localStorage.setItem(STORAGE + ".explicit", "1"); } catch {} }
  }, [lang, onPersist, userLang]);
  // Timed try-it-out of Spanish (plans without it). Lives here because the subtree remounts on every EN↔ES flip.
  const [trialEnd, setTrialEnd] = useState(0);
  const trialCb = useRef(null);
  const startTrial = useCallback((seconds, onEnd) => { trialCb.current = onEnd; setTrialEnd(Date.now() + seconds * 1000); setLang("es", { persist: false }); }, [setLang]);
  const stopTrial = useCallback(() => { trialCb.current = null; setTrialEnd(0); setLang("en", { persist: false }); }, [setLang]);
  useEffect(() => {
    if (!trialEnd) return undefined;
    const id = setTimeout(() => { setTrialEnd(0); stopDom(); setLangState("en"); setEpoch((e) => e + 1); const cb = trialCb.current; trialCb.current = null; if (cb) setTimeout(cb, 400); }, Math.max(0, trialEnd - Date.now()));
    return () => clearTimeout(id);
  }, [trialEnd]);
  const value = useMemo(() => ({ lang, setLang, t, trialEnd, startTrial, stopTrial }), [lang, setLang, trialEnd, startTrial, stopTrial]);
  return <LangCtx.Provider value={value}><div key={epoch} className="contents">{children}</div></LangCtx.Provider>;
}
